import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

const sharedAccountSchema = z.object({ mask: z.string(), type: z.string() }).strict();

export const invitationSchema = z.object({
  owner_display_name: z.string(),
  owner_email_masked: z.string(),
  invitee_email_masked: z.string(),
  tax_year: z.number().int(),
  scope: z.literal('business_only'),
  shared_accounts: z.array(sharedAccountSchema),
  expires_at: z.string(),
}).strict();

export const invitationPreviewSchema = z.object({
  status: z.enum(['pending', 'accepted', 'expired', 'revoked', 'declined']),
  invitation: invitationSchema.nullable(),
}).strict();

export type Invitation = z.infer<typeof invitationSchema>;
export type InvitationPreview = z.infer<typeof invitationPreviewSchema>;

export function useInvitationPreview(token: string | null) {
  return useQuery({
    queryKey: ['accountant-invitation-preview', token],
    enabled: Boolean(token),
    queryFn: async () => {
      const response = await client.post('/api/accountant-invitations/preview', { token });
      return parseEnvelope(invitationPreviewSchema, response.data).data;
    },
  });
}

const declineSchema = z.object({ status: z.literal('declined') }).strict();
const reportSchema = z.object({ received: z.literal(true) }).strict();

export function useDeclineInvitation() {
  return useMutation({
    mutationFn: async (input: { token: string }) => {
      const response = await client.post('/api/accountant-invitations/decline', { token: input.token });
      return parseEnvelope(declineSchema, response.data).data;
    },
  });
}

export function useReportInvitation() {
  return useMutation({
    mutationFn: async (input: { token: string; reason: 'unexpected' | 'wrong_person' | 'other' }) => {
      const response = await client.post('/api/accountant-invitations/report', { token: input.token, reason: input.reason });
      return parseEnvelope(reportSchema, response.data).data;
    },
  });
}
